import { FilePenLine, X } from 'lucide-react';
import { CustomDatePicker, CustomSelect, ModalBackdrop } from '../common';

function SalesOrderEditModal({
  values,
  errors,
  customerOptions,
  customerBranchOptions,
  seaweedTypeOptions,
  containerOptions,
  selectedContainer,
  isSaving,
  includeCustomerBranch = false,
  includeSeaweedType = false,
  includeCommercialFields = false,
  includeContainerDetails = true,
  includeWarehouseLocation = true,
  onChange,
  onClose,
  onSubmit,
}) {
  return (
    <ModalBackdrop backdropClassName="bg-[rgba(var(--theme-shadow-rgb),0.34)]">
      <div className="w-full max-w-5xl overflow-hidden rounded-[26px] border border-[var(--theme-border-soft)] bg-white shadow-[0_24px_70px_rgba(var(--theme-shadow-rgb),0.17)]">
        <div className="border-b border-[var(--theme-border-soft)] bg-white px-5 py-4 sm:px-6 sm:py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="m-0 inline-flex items-center gap-2 text-[0.78rem] font-semibold uppercase tracking-[0.09em] text-[var(--theme-text-muted)]">
                <FilePenLine className="h-4 w-4 text-[var(--theme-text-subtle)]" aria-hidden="true" />
                Sales Order Record
              </p>
              <h2 className="m-0 mt-2 text-[1.35rem] font-semibold tracking-[-0.02em] text-[var(--theme-text)]">
                Edit Sales Order
              </h2>
            </div>
            <button
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-surface-soft)]"
              type="button"
              onClick={onClose}
              disabled={isSaving}
              aria-label="Close sales order modal"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        <form onSubmit={onSubmit}>
          <div className="grid gap-4 px-5 py-5 sm:px-6 sm:py-6 md:grid-cols-2 xl:grid-cols-3">
            <div className={includeSeaweedType ? 'xl:col-start-1 xl:row-start-1' : ''}>
              <FormSelect
                label="Customer"
                name="customerId"
                value={values.customerId}
                error={errors.customerId}
                onChange={onChange}
                options={customerOptions}
                placeholder="Select customer"
              />
            </div>
            {includeCustomerBranch ? (
              <div className={includeSeaweedType ? 'xl:col-start-2 xl:row-start-1' : ''}>
                <FormSelect
                  label="Delivery Location"
                  name="branch"
                  value={values.branch}
                  error={errors.branch}
                  onChange={onChange}
                  options={customerBranchOptions}
                  placeholder="Select delivery location"
                  disabled={!values.customerId || !customerBranchOptions.length}
                />
              </div>
            ) : null}
            {includeSeaweedType ? (
              <div className="xl:col-start-3 xl:row-start-1">
                <FormSelect
                  label="Seaweed Type"
                  name="seaweedType"
                  value={values.seaweedType}
                  error={errors.seaweedType}
                  onChange={onChange}
                  options={seaweedTypeOptions}
                  placeholder="Select seaweed type"
                  disabled={!seaweedTypeOptions.length}
                />
              </div>
            ) : null}
            {includeCommercialFields ? (
              <div className={includeSeaweedType ? 'xl:col-start-1 xl:row-start-2' : ''}>
                <FormInput
                  label="FCL Quantity"
                  name="quantity"
                  value={values.quantity}
                  error={errors.quantity}
                  onChange={onChange}
                  type="number"
                  min="0"
                  step="0.01"
                />
              </div>
            ) : null}
            {includeContainerDetails ? (
              <>
                <FormInput
                  label="FCL Batch Number"
                  name="fclBatchNumber"
                  value={values.fclBatchNumber}
                  error={errors.fclBatchNumber}
                  onChange={onChange}
                />
                <FormInput
                  label="Code Number"
                  name="codeNumber"
                  value={values.codeNumber}
                  error={errors.codeNumber}
                  onChange={onChange}
                />
                <FormInput
                  label="Container Number"
                  name="containerNumber"
                  value={values.containerNumber}
                  error={errors.containerNumber}
                  onChange={onChange}
                />
              </>
            ) : null}
            <div className={includeCommercialFields ? 'xl:col-start-2 xl:row-start-2' : ''}>
              <CustomDatePicker
                label="S.O. Date"
                name="soDate"
                value={values.soDate}
                error={errors.soDate}
                onChange={onChange}
              />
            </div>
            {includeWarehouseLocation ? (
              <FormInput
                label="Warehouse Location / Branch"
                name="warehouseLocation"
                value={values.warehouseLocation}
                error={errors.warehouseLocation}
                onChange={onChange}
              />
            ) : null}
            {includeContainerDetails ? (
              <>
                <FormSelect
                  label="Seaweeds Container"
                  name="sourcePurchaseOrderId"
                  value={values.sourcePurchaseOrderId}
                  error={errors.sourcePurchaseOrderId}
                  onChange={onChange}
                  options={containerOptions}
                  placeholder="Select completed seaweeds container"
                />
                <ReadOnlyField
                  label="Supplier Name"
                  value={selectedContainer?.supplierName || '-'}
                />
                <ReadOnlyField
                  label="Sacks Mark"
                  value={selectedContainer?.sacksMark || '-'}
                />
              </>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-[var(--theme-border-soft)] bg-white px-5 py-4 sm:px-6">
            <button
              className="inline-flex min-h-[46px] items-center justify-center rounded-[14px] border border-[var(--theme-border)] bg-white px-6 text-[0.95rem] font-semibold text-[var(--theme-text-subtle)] shadow-[0_4px_12px_rgba(var(--theme-shadow-rgb),0.06)] outline-none transition hover:bg-[var(--theme-surface-soft)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              type="button"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="inline-flex min-h-[46px] items-center justify-center rounded-[14px] bg-[var(--theme-accent)] px-7 text-[0.95rem] font-semibold text-white shadow-[0_12px_24px_rgba(var(--theme-shadow-rgb),0.2)] outline-none transition hover:bg-[var(--theme-accent-strong)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </ModalBackdrop>
  );
}

function FormInput({
  label,
  name,
  value,
  error,
  onChange,
  type = 'text',
  ...inputProps
}) {
  return (
    <label className="grid w-full min-w-0 gap-1.5">
      <span className="text-[0.74rem] font-medium text-[var(--theme-text-subtle)]">{label}</span>
      <input
        className={`min-h-[40px] w-full min-w-0 rounded-full border bg-[var(--theme-surface-warm)] px-3 py-2 text-[0.84rem] outline-none transition ${
          error
            ? 'border-[#d78b7d] focus:border-[#c76552]'
            : 'border-[var(--theme-border)] focus:border-[var(--theme-accent)]'
        }`}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        {...inputProps}
      />
      {error ? <span className="text-[0.82rem] text-[#bb5745]">{error}</span> : null}
    </label>
  );
}

function FormSelect({
  label,
  name,
  value,
  error,
  onChange,
  options,
  placeholder,
  disabled = false,
}) {
  return (
    <CustomSelect
      label={label}
      name={name}
      value={value}
      error={error}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div className="grid w-full min-w-0 gap-1.5">
      <span className="text-[0.74rem] font-medium text-[var(--theme-text-subtle)]">{label}</span>
      <div className="flex min-h-[40px] w-full min-w-0 items-center rounded-full border border-[var(--theme-border-soft)] bg-white px-3 py-2 text-[0.84rem] text-[var(--theme-text-subtle)]">
        {value}
      </div>
    </div>
  );
}

export default SalesOrderEditModal;
